/* eslint-disable @typescript-eslint/ban-ts-comment */
// @ts-nocheck

import { gsap } from 'gsap';
import {
    BufferAttribute,
    BufferGeometry,
    Color,
    type Material,
    Object3D,
    Points,
    ShaderMaterial,
    Vector3,
} from 'three';

import PerformanceProfile from '../../../../utils/PerformanceProfile';

import { fragmentShader, vertexShader } from './shaders';

const GLOBAL_CONFIG = {
    range: { x: 1, y: 1, z: 3 },
    size: 35,
    alpha: 1.0,
};

const CONFIG = [
    {
        // Tier0
        color: '#ffffff',
    },
    {
        // Tier1
        color: '#789eff',
    },
    {
        // Tier2

        color: '#b578ff',
    },
    {
        // Tier3
        color: '#ffc701',
    },
];

let GUI;

const addGUI = async () => {
    await PerformanceProfile.devModeReady();

    GUI = PerformanceProfile.GUI.floorGameUp.addFolder({
        title: 'Particles',
        expanded: false,
    });

    GUI.addButton({ title: 'Export Config' }).on('click', () => {
        PerformanceProfile.exportConfig('Particles', { ...GLOBAL_CONFIG, ...CONFIG });
    });

    GUI.addInput(GLOBAL_CONFIG, 'range');
    GUI.addInput(GLOBAL_CONFIG, 'alpha', { min: 0, max: 1, step: 0.001 });
    GUI.addInput(GLOBAL_CONFIG, 'size', { min: 10, max: 150, step: 0.5 });

    for (let i = 0; i < CONFIG.length; i++) {
        const folder = GUI.addFolder({
            title: `Tier ${i + 1}`,
            expanded: false,
        });

        folder.addInput(CONFIG[i], 'color');
    }
};

if (PerformanceProfile.devMode) addGUI();

interface CardParticlesOptions {
    tier: number;
    isCollectionView?: boolean;
}

export default class CardParticles extends Object3D {
    tier: number;
    isCollectionView: boolean;
    rangeMultiplier: number;

    count = 200;
    time = 0.0;
    visible = false;

    geometry?: BufferGeometry;
    material?: Material;
    mesh?: Points;

    // TODO: None of these properties are used in this class.
    color: string;

    constructor({ tier, isCollectionView }: CardParticlesOptions) {
        super();

        this.tier = tier;
        this.isCollectionView = Boolean(isCollectionView);

        this.rangeMultiplier = this.isCollectionView ? 7.0 : 1.0;

        this.initMaterial();
        this.initGeometry();
        this.initMesh();

        if (PerformanceProfile.devMode) {
            GUI.on('change', this.updateUniforms.bind(this));
        }
    }

    initMaterial() {
        // this.material = new MeshBasicMaterial();

        this.material = new ShaderMaterial({
            vertexShader,
            fragmentShader,
            uniforms: {
                uRange: {
                    value: new Vector3(
                        GLOBAL_CONFIG.range.x * this.rangeMultiplier,
                        GLOBAL_CONFIG.range.y,
                        GLOBAL_CONFIG.range.z,
                    ),
                },
                uColor: { value: new Color(CONFIG[this.tier].color) },
                uSize: { value: GLOBAL_CONFIG.size },
                uAlpha: { value: GLOBAL_CONFIG.alpha },
                uTime: { value: Math.random() * Math.PI * 2.0 },
                uTransition: { value: 1.0 },
            },
            transparent: true,
            depthWrite: false,
        });
    }

    initGeometry() {
        this.geometry = new BufferGeometry();

        const offsets: number[] = [];
        const randoms: number[] = [];
        const randomsFrag: number[] = [];

        for (let i = 0; i < this.count; i++) {
            randoms.push(Math.random(), Math.random(), Math.random(), Math.random());
            randomsFrag.push(Math.random(), Math.random(), Math.random(), Math.random());
            offsets.push(Math.random() * 2.0 - 1.0, Math.random() * 2.0 - 1.0, Math.random());
        }

        this.geometry.setAttribute('position', new BufferAttribute(new Float32Array(offsets), 3));
        this.geometry.setAttribute('random', new BufferAttribute(new Float32Array(randoms), 4));
        this.geometry.setAttribute('randomFrag', new BufferAttribute(new Float32Array(randomsFrag), 4));
    }

    initMesh() {
        this.mesh = new Points(this.geometry, this.material);
        this.add(this.mesh);
    }

    update(elapsed: number, _delta) {
        this.material.uniforms.uTime.value = elapsed * 1.5;
    }

    animateIn() {
        this.visible = true;

        gsap.fromTo(
            this.material.uniforms.uTransition,
            {
                value: 0.0,
            },
            {
                onStart: () => {
                    this.visible = true;
                },
                value: 1.0,
                duration: 3.0,
                delay: 2.0,
                // ease: 'elastic.out(3, 1)'
                ease: 'power2.inOut',
            },
        );
    }

    updateUniforms() {
        this.material.uniforms.uAlpha.value = GLOBAL_CONFIG.alpha;
        this.material.uniforms.uSize.value = GLOBAL_CONFIG.size;
        this.material.uniforms.uRange.value.x = GLOBAL_CONFIG.range.x * this.rangeMultiplier;
        this.material.uniforms.uRange.value.y = GLOBAL_CONFIG.range.y;
        this.material.uniforms.uRange.value.z = GLOBAL_CONFIG.range.z;

        this.material.uniforms.uColor.value.setStyle(CONFIG[this.tier].color);
    }

    delete() {
        this.material.dispose();
        this.geometry.dispose();
    }
}
